import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../../../../util/reactIntl';
import { ExternalLink, NamedLink } from '../../../../../components';
import css from './PriorityLinks.module.css';

export const CreateListingMenuLink = props => {
  const { hasOrganizationName, handleMissingInfoModal, currentUser } = props;

  const isBuyer =
    currentUser &&
    currentUser.attributes &&
    currentUser.attributes.permissions &&
    currentUser.attributes.permissions.postListings === 'permission/deny';
  const isUnauthenticated = !(currentUser && currentUser.id);

  if (isUnauthenticated) {
    return (
      <div className={props.customLinksMenuClass}>
        <NamedLink name="SignupPage" className={classNames(css.priorityLink, css.highlight)}>
          <span className={css.priorityLinkLabel}>
            <FormattedMessage id="TopbarDesktop.createListing" />
          </span>
        </NamedLink>
      </div>
    );
  }

  return (
    <div className={props.customLinksMenuClass}>
      {hasOrganizationName ? (
        <NamedLink
          name="NewListingPage"
          className={classNames(css.priorityLink, css.highlight)}
          to={{
            state: {
              isSeller: true,
            },
          }}
        >
          <span className={css.priorityLinkLabel}>
            {!isBuyer && <FormattedMessage id="TopbarDesktop.createListing" />}
          </span>
        </NamedLink>
      ) : (
        <div
          className={classNames(css.priorityLink, css.highlight)}
          onClick={handleMissingInfoModal}
          style={{ pointerEvents: isBuyer ? 'none' : 'auto', opacity: isBuyer ? 0.5 : 1 }}
        >
          {!isBuyer && <FormattedMessage id="TopbarDesktop.createListing" />}
        </div>
      )}
    </div>
  );
};

CreateListingMenuLink.defaultProps = {
  currentUser: {
    attributes: {
      permissions: {
        postListings: false,
      },
    },
  },
};

CreateListingMenuLink.propTypes = {
  hasOrganizationName: PropTypes.bool.isRequired,
  handleMissingInfoModal: PropTypes.func.isRequired,
  currentUser: PropTypes.shape({
    attributes: PropTypes.shape({
      permissions: PropTypes.shape({
        postListings: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
      }),
    }),
  }),
};

const PriorityLink = ({ linkConfig }) => {
  const { text, type, href, route, highlight } = linkConfig;
  const classes = classNames(css.priorityLink, { [css.highlight]: highlight });

  if (type === 'internal' && route) {
    const { name, params, to } = route || {};
    return (
      <NamedLink name={name} params={params} to={to} className={classes}>
        <span className={css.priorityLinkLabel}>{text}</span>
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={classes}>
      <span className={css.priorityLinkLabel}>{text}</span>
    </ExternalLink>
  );
};

const PriorityLinks = props => {
  const containerRef = useRef(null);

  useEffect(() => {
    const isMeasured = props.links?.[0]?.width;
    if (containerRef.current && !isMeasured) {
      const linksFromRenderedWrapper = [...containerRef.current.childNodes];
      let cumulatedWidth = 0;
      const linksWithWidths = props.links.reduce((links, l, i) => {
        const width = linksFromRenderedWrapper[i].offsetWidth;
        cumulatedWidth = cumulatedWidth + width;
        return [...links, { ...l, width, cumulatedWidth }];
      }, []);
      props.setLinks(linksWithWidths);
    }
  }, [containerRef]);

  const { links, priorityLinks } = props;
  const isServer = typeof window === 'undefined';
  const isMeasured = links?.[0]?.width && (priorityLinks.length === 0 || priorityLinks?.[0]?.width);
  const styleWrapper = !!isMeasured
    ? {}
    : {
        style: {
          position: 'absolute',
          top: '-2000px',
          left: '-2000px',
          width: '100%',
          height: 'var(--topbarHeightDesktop)',
          display: 'flex',
          flexDirection: 'row',
        },
      };
  const linkConfigs = isMeasured ? priorityLinks : links;

  return isMeasured || isServer ? (
    <div className={css.priorityLinkWrapper} {...styleWrapper} ref={containerRef}>
      {linkConfigs.map(linkConfig => {
        return <PriorityLink key={linkConfig.text} linkConfig={linkConfig} />;
      })}
    </div>
  ) : (
    ReactDOM.createPortal(
      <div className={css.priorityLinkWrapper} {...styleWrapper} ref={containerRef}>
        {linkConfigs.map(linkConfig => {
          return <PriorityLink key={linkConfig.text} linkConfig={linkConfig} />;
        })}
      </div>,
      document.body
    )
  );
};

PriorityLinks.defaultProps = {
  currentUser: {
    attributes: {
      permissions: {
        postListings: false,
      },
    },
  },
};

PriorityLinks.propTypes = {
  links: PropTypes.array.isRequired,
  priorityLinks: PropTypes.array.isRequired,
  setLinks: PropTypes.func.isRequired,
  currentUser: PropTypes.shape({
    attributes: PropTypes.shape({
      permissions: PropTypes.shape({
        postListings: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
      }),
    }),
  }),
};

export default PriorityLinks;
